import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  getUserSave, 
  createSave, 
  updateSave
} from '../services/gameData';

/**
 * Hook for managing game data
 * @param {string} userId - User ID from Supabase
 * @param {string} sessionToken - Secure Radcoon session token
 * @returns {Object} Game data state and methods
 */
export function useGameData(userId, sessionToken = null) {
  const [gameSave, setGameSave] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastIncomeUpdate, setLastIncomeUpdate] = useState(null);
  const [gameConstants, setGameConstants] = useState(null);
  const [loadedSession, setLoadedSession] = useState({ userId: null, sessionToken: null });
  const previousBalanceRef = useRef(null);
  const previousSaveIdRef = useRef(null);
  const activeSaveId = gameSave?.id;
  const hasPendingSessionLoad = Boolean(
    userId &&
    sessionToken &&
    (loadedSession.userId !== userId || loadedSession.sessionToken !== sessionToken)
  );

  const getBalanceStorageKey = useCallback((saveId) => {
    return saveId ? `dckslap-game-balance:${saveId}` : null;
  }, []);

  const readStoredBalance = useCallback((saveId) => {
    if (!saveId || typeof window === 'undefined') {
      return null;
    }

    const storageKey = getBalanceStorageKey(saveId);
    const rawBalance = storageKey ? window.localStorage.getItem(storageKey) : null;
    const parsedBalance = Number(rawBalance);

    return Number.isFinite(parsedBalance) ? parsedBalance : null;
  }, [getBalanceStorageKey]);

  const writeStoredBalance = useCallback((save) => {
    if (!save?.id || typeof window === 'undefined') {
      return;
    }

    const storageKey = getBalanceStorageKey(save.id);
    if (!storageKey) {
      return;
    }

    window.localStorage.setItem(storageKey, String(Number(save.money || 0)));
  }, [getBalanceStorageKey]);

  const trackIncomeFromBalanceDelta = useCallback((save) => {
    if (!save?.id) {
      return;
    }

    const currentBalance = Number(save.money || 0);
    const previousBalance =
      previousSaveIdRef.current === save.id && previousBalanceRef.current !== null
        ? previousBalanceRef.current
        : readStoredBalance(save.id);

    if (previousBalance === null || !Number.isFinite(previousBalance)) {
      return;
    }

    const income = currentBalance - previousBalance;

    if (income > 0) {
      setLastIncomeUpdate({
        income,
        previousBalance,
        currentBalance,
        timestamp: Date.now(),
      });
    }
  }, [readStoredBalance]);
  
  const storeSaveState = useCallback((save) => {
    previousSaveIdRef.current = save?.id || null;
    previousBalanceRef.current = save ? Number(save.money || 0) : null;
    writeStoredBalance(save);
  }, [writeStoredBalance]);
  
  // Load user's save
  const loadSave = useCallback(async (options = {}) => {
    const { silent = false } = options;

    if (!userId || !sessionToken) {
      storeSaveState(null);
      setGameSave(null);
      setGameConstants(null);
      setLastIncomeUpdate(null);
      setLoadedSession({ userId: null, sessionToken: null });
      setIsLoading(false);
      return null;
    }
    
    if (!silent) {
      setIsLoading(true);
    }
    setError(null);
    
    try {
      const { save, constants, error } = await getUserSave(userId, sessionToken);
      
      if (error) {
        setError(error.message || 'Failed to load game save');
        setLoadedSession({ userId, sessionToken });
        if (!silent) {
          setIsLoading(false);
        }
        return null;
      }
      
      // Store game constants returned from the backend
      if (constants) {
        setGameConstants(constants);
      }
      
      // If a save was found, attach the constants to it
      if (save) {
        save.constants = constants;
      }

      trackIncomeFromBalanceDelta(save);
      
      storeSaveState(save);
      setGameSave(save);
      setLoadedSession({ userId, sessionToken });
      if (!silent) {
        setIsLoading(false);
      }
      
      return save;
    } catch (e) {
      console.error('Error loading save:', e);
      setError(e.message || 'Unexpected error loading save');
      setLoadedSession({ userId, sessionToken });
      if (!silent) {
        setIsLoading(false);
      }
      return null;
    }
  }, [userId, sessionToken, storeSaveState, trackIncomeFromBalanceDelta]);
  
  // Create a new save
  const createNewSave = useCallback(async (companyName) => {
    if (!userId) {
      setError('User not authenticated');
      return null;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const { save, constants, error } = await createSave(userId, companyName, {}, sessionToken);
      
      if (error) {
        // If user already has a save, load it instead
        if (error === 'User already has a save game' && save) {
          // Update game constants if they were returned
          if (constants) {
            setGameConstants(constants);
            save.constants = constants;
          }
          
          storeSaveState(save);
          setGameSave(save);
          setIsLoading(false);
          return save;
        }
        
        setError(error.message || 'Failed to create game save');
        setIsLoading(false);
        return null;
      }
      
      // Update game constants if they were returned
      if (constants) {
        setGameConstants(constants);
        save.constants = constants;
      }
      
      storeSaveState(save);
      setGameSave(save);
      setIsLoading(false);
      return save;
    } catch (e) {
      console.error('Error creating save:', e);
      setError(e.message || 'Unexpected error creating save');
      setIsLoading(false);
      return null;
    }
  }, [userId, sessionToken, storeSaveState]);
  
  // Update game data
  const updateGameData = useCallback(async (newData) => {
    if (!gameSave || !gameSave.id) {
      setError('No active game save');
      return false;
    }
    
    if (!newData) {
      setError('No game data provided');
      return false;
    }
    
    setIsLoading(true);
    
    try {
      const { save, constants, error } = await updateSave(gameSave.id, newData, sessionToken);
      
      if (error) {
        setError(error.message || 'Failed to update game data');
        setIsLoading(false);
        return false;
      }
      
      // Update game constants if they were returned
      if (constants) {
        setGameConstants(constants);
        save.constants = constants;
      } else if (gameConstants) {
        // Preserve existing constants if none were returned
        save.constants = gameConstants;
      }
      
      storeSaveState(save);
      setGameSave(save);
      setIsLoading(false);
      return true;
    } catch (e) {
      console.error('Error updating game data:', e);
      setError(e.message || 'Unexpected error updating game data');
      setIsLoading(false);
      return false;
    }
  }, [gameSave, gameConstants, sessionToken, storeSaveState]);
  
  // Buy a mining rig - this is now handled directly by the updateMiningRigs endpoint
  // and called from MiningRigMenu.js
  
  // Load save on mount and when userId changes
  useEffect(() => {
    loadSave();
  }, [userId, sessionToken, loadSave]);

  // Update money every 2 minutes if connected
  useEffect(() => {
    if (!userId || !sessionToken || !activeSaveId) return;

    const updateInterval = setInterval(async () => {
      try {
        const { save, constants, error } = await getUserSave(userId, sessionToken);
        if (error) {
          console.error('Error updating game data:', error);
          return;
        }

        if (constants) {
          setGameConstants(constants);
          save.constants = constants;
        }

        trackIncomeFromBalanceDelta(save);

        storeSaveState(save);
        setGameSave(save);
      } catch (e) {
        console.error('Error in game data update interval:', e);
      }
    }, 2 * 60 * 1000); // 2 minutes in milliseconds

    return () => clearInterval(updateInterval);
  }, [userId, sessionToken, activeSaveId, storeSaveState, trackIncomeFromBalanceDelta]);
  
  return {
    gameSave,
    isLoading: isLoading || hasPendingSessionLoad,
    error,
    loadSave,
    createNewSave,
    updateGameData,
    lastIncomeUpdate,
    hasSave: !!gameSave,
    gameConstants
  };
} 
